/**
 * MQTT over WebSocket, authenticated with an Auth0 access token.
 *
 * THE BROKER DISCONNECTS ON TOKEN EXPIRY. `disconnect_after_expire = true` means a live
 * session is closed the moment its token dies — deliberate, so a connection cannot
 * outlive its grant. The consequence here is that reconnecting with the SAME token is
 * guaranteed to fail, so every reconnect fetches a FRESH one. mqtt.js's built-in
 * reconnect would re-send the dead credential forever, which is why it is disabled and
 * reconnection is driven from here.
 */
import mqtt, { MqttClient } from "mqtt";
import { cfg } from "./config";
import { qos, retain, type ChannelName } from "./contract/fleetComms";

export type ConnState =
  | "connecting"
  | "connected"
  | "reconnecting"
  /** No token could be obtained — an Auth0 problem. */
  | "unauthorized"
  /** A token WAS obtained and the broker refused it — a broker/claims problem. */
  | "rejected"
  | "offline";

export interface Handlers {
  onState: (s: ConnState, detail?: string) => void;
  onMessage: (topic: string, payload: Uint8Array) => void;
  /** Fetch a token, or the reason one is unavailable. */
  getToken: () => Promise<{ token: string } | { error: string }>;
  /** The topic filters this token may subscribe to. */
  getFilters: (token: string) => string[];
  getUsername: () => Promise<string>;
}

export class Connection {
  private client: MqttClient | null = null;
  private stopped = false;
  private attempt = 0;

  constructor(private h: Handlers) {}

  async start(): Promise<void> {
    this.stopped = false;
    await this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.client?.end(true);
    this.client = null;
  }

  /**
   * Publish, reporting whether it actually went out.
   *
   * Returns a reason on failure rather than throwing, because every caller here is a
   * button: "not connected" is a thing to show next to the control the user just
   * pressed, not an exception to unwind into.
   *
   * NOTE what this CANNOT tell you. The broker drops a publish outside the token's grant
   * silently (`deny_action = ignore`), so a `null` here means "handed to the broker",
   * never "accepted". That is why the debug surface rides `cmd/`, which the operator
   * grant already covers — a bespoke branch would fail exactly this quietly.
   */
  publish(topic: string, payload: unknown, channel: ChannelName): string | null {
    if (!this.client?.connected) return "not connected to the broker";
    try {
      // FROM THE CONTRACT, not from here. Both channels this app publishes happen to be
      // QoS 1 today, so a hardcoded 1 was right by luck — and would have stayed right
      // until the first channel that was not, which is the kind of drift the binding
      // exists to make impossible.
      this.client.publish(topic, JSON.stringify(payload),
                          { qos: qos(channel) as 0 | 1 | 2, retain: retain(channel) });
      return null;
    } catch (e) {
      return String((e as Error)?.message ?? e);
    }
  }

  private async connect(): Promise<void> {
    if (this.stopped) return;
    const result = await this.h.getToken();
    if ("error" in result) {
      // Terminal for the CONNECTION — no point retrying without a token. Whether the
      // SESSION is over is the caller's decision, not ours: a token that cannot be
      // renewed right now is not the same as a user who should be signed out.
      this.h.onState("unauthorized", result.error);
      return;
    }
    const token = result.token;
    // Ask for exactly what the token grants — see subscribeFiltersFromToken. Asking for
    // a broader wildcard is refused outright and yields no data at all.
    const filters = this.h.getFilters(token);
    const username = await this.h.getUsername();
    this.h.onState(this.attempt === 0 ? "connecting" : "reconnecting");

    const client = mqtt.connect(cfg.brokerUrl, {
      username,                 // ignored for auth; makes broker logs legible
      password: token,          // THE TOKEN IS THE PASSWORD
      protocolVersion: 5,
      clean: true,
      // Off deliberately — see the file header. We reconnect ourselves, with a new token.
      reconnectPeriod: 0,
      connectTimeout: 15_000,
    });
    this.client = client;

    client.on("connect", () => {
      this.attempt = 0;
      this.h.onState("connected");
      if (filters.length === 0) {
        // Connected, authenticated, and granted nothing. Not an error — the correct
        // outcome for an account with no site grants, and worth saying plainly.
        this.h.onState("connected", "no site grants — nothing to subscribe to");
        return;
      }
      client.subscribe(filters, { qos: 1 }, (err, granted) => {
        // A refused SUBSCRIBE does NOT mean the connection failed. The session is live;
        // the broker declined this filter. Reporting it as "offline" pointed at the
        // network when the answer was in the access table.
        if (err) {
          this.h.onState("connected", `subscribe refused: ${err.message}`);
          return;
        }
        const ok = (granted ?? []).filter((g) => g.qos <= 2).map((g) => g.topic);
        if (ok.length === 0) {
          this.h.onState("connected", "subscriptions refused by the broker");
        } else {
          this.h.onState("connected", `watching ${ok.length} filter(s)`);
        }
      });
    });

    client.on("message", (topic, payload) => this.h.onMessage(topic, payload));

    client.on("error", (err) => {
      const msg = String(err?.message ?? err);
      // The broker refuses a bad/expired token at CONNECT. Distinguish it from a network
      // failure: one needs a new token, the other needs patience, and they look the same
      // from a browser otherwise.
      // A CONNACK rejection means the token was OBTAINED and the BROKER said no —
      // an entirely different problem from being unable to get one, and reporting it as
      // "Auth0 reported: ..." sends people to the wrong system entirely.
      const rejected = /not authorized|bad user|unauthorized|connack|not authoris/i.test(msg);
      this.h.onState(rejected ? "rejected" : "offline", msg);
    });

    client.on("close", () => {
      if (this.stopped) return;
      // Expected once the token expires. Reconnect with a fresh one, backing off so a
      // broker that is genuinely down is not hammered.
      this.attempt += 1;
      const delay = Math.min(30_000, 1000 * 2 ** Math.min(this.attempt, 5));
      this.h.onState("reconnecting", `retrying in ${Math.round(delay / 1000)}s`);
      setTimeout(() => void this.connect(), delay);
    });
  }
}
